import { type ReactElement, createElement } from "react"
import { createRoot } from "react-dom/client"

import { getRuntimeConfig } from "@/shared/config/env"
import { type InitI18nOptions, initI18n } from "@/shared/i18n"
import {
  type LocaleCode,
  getLang,
  localeToHtmlLang,
  setPageLocales,
} from "@/shared/i18n/lang"
import "@/shared/styles/antd-mobile.css"
import "@/shared/styles/tailwind.css"
import { setupTrack, track } from "@/shared/utils/track"
import { ConfigProvider } from "antd-mobile"
import enUS from "antd-mobile/es/locales/en-US"
import zhCN from "antd-mobile/es/locales/zh-CN"

export interface PageMeta {
  /** 页面标识，用于埋点 page 字段 */
  name: string
  /** i18n key，页面挂载后按当前语言覆盖 <title> */
  titleKey?: string
  /**
   * 本页支持的语言，默认中 / 英。
   * 需要更多语言（如官网的柬埔寨语）时显式传入，例如 ["zh_CN", "en_US", "km_KH"]
   */
  locales?: LocaleCode[]
  /**
   * 本页默认语言，必须包含在 locales 内。
   * 只在「URL 无 ?lang= 且用户没有选过语言」时生效，优先级高于运行期 defaultLocale。
   */
  defaultLocale?: LocaleCode
  /**
   * 是否按系统 / 浏览器语言自动匹配，默认 true。
   * 传 false 时页面不再跟随系统语言（如官网面向柬埔寨用户，固定默认柬语）。
   */
  detectSystemLang?: boolean
  /** 页面私有文案，按语言合并到共享文案之上 */
  messages?: InitI18nOptions["messages"]
  /** 返回页面根元素 */
  render: () => ReactElement
}

/**
 * 落地页统一入口。每个页面 main.tsx 只调用一次：
 *
 *   createPage({ name: "promo", titleKey: "promo_meta_title", render: () => <PromoPage /> })
 *
 * 负责：声明页面语言范围、初始化 i18n、antd-mobile ConfigProvider + 语言包、
 * 埋点环境、document.title。
 */
export function createPage(meta: PageMeta): void {
  const runtime = getRuntimeConfig()
  setupTrack(runtime)

  // 必须在 initI18n 之前：getLang() 会按页面语言范围过滤系统语言，并应用页面默认语言
  setPageLocales(meta.locales, {
    defaultLocale: meta.defaultLocale,
    detectSystemLang: meta.detectSystemLang,
  })

  const i18n = initI18n({ messages: meta.messages })
  const applyTitle = () => {
    if (!meta.titleKey) return
    const text = i18n.exists(meta.titleKey) ? i18n.t(meta.titleKey) : ""
    if (text) document.title = text
  }

  applyTitle()

  i18n.on("languageChanged", applyTitle)

  const locale = getLang()
  const antdLocale = locale === "zh_CN" ? zhCN : enUS
  // 修正 <html lang>，首屏模板给的是默认值
  document.documentElement.lang = localeToHtmlLang(locale)

  const container = document.getElementById("root")
  if (!container) {
    throw new Error("[createPage] 找不到 #root 挂载点")
  }

  // 页面曝光埋点
  track("page_view", { page: meta.name })

  const root = createRoot(container)

  // 用 createElement 而非 JSX，保证本文件保持 .ts（无 JSX），与 tsc 的 .ts/.tsx 语义一致
  root.render(
    createElement(ConfigProvider, { locale: antdLocale }, meta.render()),
  )
}
